import React from "react";
import { Helmet } from "react-helmet";
import { useLocation, useParams } from "react-router-dom";
import {
    FaLinkedin,
    FaGithub,
    FaXTwitter,
    FaFacebook,
    FaInstagram,
    FaYoutube,
    FaGlobe
} from "react-icons/fa6";

export default function AuthorProfile() {
    const { state } = useLocation();
    const { slug } = useParams();

    // ===============================
    // DYNAMIC DATA (from ArticlePage)
    // ===============================
    const dynamicAuthor = state?.author;

    if (!dynamicAuthor) {
        return null; // or loader / redirect
    }

    // ===============================
    // STATIC AUTHOR DETAILS
    // ===============================
    const staticAuthor = {
        role: "Senior Python Developer & Generative AI Engineer",

        bioShort:
            "Naveen Kumar is a senior Python developer with over 7 years of experience in building scalable web applications, cloud-based solutions, and Generative AI systems.",

        bioLong: [
            "Naveen Kumar is an experienced Python developer with more than seven years of professional experience in software analysis, design, development, implementation, and maintenance of complex web applications.",
            "His expertise includes Python, Django, Flask, RESTful APIs, microservices architecture, along with strong hands-on experience in AWS and Azure cloud platforms.",
            "He currently works on Generative AI and Natural Language Processing solutions using LangChain, OpenAI, and Azure OpenAI services.",
            "Through Tech Article Hub, he shares practical programming tutorials and real-world technology insights."
        ],

        skills: [
            "Python Development",
            "Django & Flask",
            "REST APIs & Microservices",
            "AWS & Azure Cloud",
            "Generative AI & NLP",
            "LangChain & OpenAI",
            "Machine Learning",
            "Docker & DevOps",
            "PostgreSQL",
            "Technical Writing"
        ]
    };

    const socials = dynamicAuthor.socials || {};

    return (
        <>
            {/* ================= SEO ================= */}
            <Helmet>
                <title>{dynamicAuthor.name} – Author Profile | Tech Article Hub</title>
                <meta
                    name="description"
                    content={`${dynamicAuthor.name} is a senior Python developer and Generative AI engineer sharing practical programming tutorials and technology insights.`}
                />
                <meta name="robots" content="index, follow" />
                <link
                    rel="canonical"
                    href={`https://techarticlehub.com/author/${slug}`}
                />

                {/* Author Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Person",
                        name: dynamicAuthor.name,
                        jobTitle: staticAuthor.role,
                        url: `https://techarticlehub.com/author/${slug}`,
                        worksFor: {
                            "@type": "Organization",
                            name: "Tech Article Hub"
                        },
                        sameAs: Object.values(socials).filter(Boolean),
                        knowsAbout: staticAuthor.skills
                    })}
                </script>
            </Helmet>

            {/* ================= HERO ================= */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 text-white">
                <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center gap-10">
                    <img
                        src={dynamicAuthor.image || "https://via.placeholder.com/300"}
                        alt={dynamicAuthor.name}
                        className="w-36 h-36 rounded-2xl border-4 border-white shadow-xl object-cover"
                    />

                    <div>
                        <h1 className="text-4xl font-extrabold">
                            {dynamicAuthor.name}
                        </h1>

                        <p className="mt-2 text-lg opacity-90">
                            {staticAuthor.role}
                        </p>

                        <p className="mt-5 max-w-2xl text-white/90 leading-relaxed">
                            {staticAuthor.bioShort}
                        </p>
                    </div>
                </div>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="max-w-6xl mx-auto px-6 py-14">
                {/* ABOUT */}
                <div className="bg-white rounded-2xl shadow-md p-8 border border-red-100">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        About the Author
                    </h2>

                    {staticAuthor.bioLong.map((para, index) => (
                        <p key={index} className="text-gray-700 leading-relaxed mb-4">
                            {para}
                        </p>
                    ))}
                </div>

                {/* SKILLS */}
                <div className="mt-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">
                        Areas of Expertise
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {staticAuthor.skills.map((skill) => (
                            <div
                                key={skill}
                                className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center font-medium text-red-700"
                            >
                                {skill}
                            </div>
                        ))}
                    </div>
                </div>

                {/* SOCIAL LINKS (DYNAMIC) */}
                <div className="mt-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Connect
                    </h2>

                    <div className="flex gap-4 flex-wrap">
                        {dynamicAuthor.linkedin && (
                            <SocialButton
                                href={dynamicAuthor.linkedin}
                                icon={<FaLinkedin />}
                                label="LinkedIn"
                                color="blue"
                            />
                        )}
                        {dynamicAuthor.github && (
                            <SocialButton
                                href={dynamicAuthor.github}
                                icon={<FaGithub />}
                                label="GitHub"
                                color="gray"
                            />
                        )}
                        {dynamicAuthor.twitter && (
                            <SocialButton
                                href={dynamicAuthor.twitter}
                                icon={<FaXTwitter />}
                                label="X"
                                color="black"
                            />
                        )}
                        {dynamicAuthor.facebook && (
                            <SocialButton
                                href={dynamicAuthor.facebook}
                                icon={<FaFacebook />}
                                label="Facebook"
                                color="blue"
                            />
                        )}
                        {dynamicAuthor.instagram && (
                            <SocialButton
                                href={dynamicAuthor.instagram}
                                icon={<FaInstagram />}
                                label="Instagram"
                                color="pink"
                            />
                        )}
                        {dynamicAuthor.youtube && (
                            <SocialButton
                                href={dynamicAuthor.youtube}
                                icon={<FaYoutube />}
                                label="YouTube"
                                color="red"
                            />
                        )}
                        {dynamicAuthor.website && (
                            <SocialButton
                                href={dynamicAuthor.website}
                                icon={<FaGlobe />}
                                label="Website"
                                color="green"
                            />
                        )}
                    </div>
                </div>

                {/* ARTICLES */}
                <div className="mt-14">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Articles by {dynamicAuthor.name}
                    </h2>

                    <p className="text-gray-600">
                        Articles written by this author will appear here.
                    </p>
                </div>
            </div>
        </>
    );
}

/* ===============================
   Reusable Social Button
   =============================== */
function SocialButton({ href, icon, label, color }) {
    const colorMap = {
        blue: "border-blue-600 text-blue-600 hover:bg-blue-600",
        gray: "border-gray-800 text-gray-800 hover:bg-gray-800",
        black: "border-black text-black hover:bg-black",
        red: "border-red-600 text-red-600 hover:bg-red-600",
        pink: "border-pink-600 text-pink-600 hover:bg-pink-600",
        green: "border-green-600 text-green-600 hover:bg-green-600"
    };

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`
        flex items-center gap-2
        px-4 py-2 rounded-lg
        border ${colorMap[color]}
        hover:text-white transition
      `}
        >
            <span className="text-xl">{icon}</span>
            <span className="text-sm font-semibold">{label}</span>
        </a>
    );
}
